import type React from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { isJsonObject, joinList, parsePositiveIntList, splitList, type JsonObject } from '../utils';

interface VideoModelConfigFieldsProps {
  /** The mapping's config JSON string (edited in place, key `video`). */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function parse(value: string): JsonObject | null {
  if (!value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return isJsonObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Per-model video limits (`CapabilityModel.config.video`): they narrow the capability limits
 * for this model. Empty fields are removed so the capability values apply.
 */
export const VideoModelConfigFields: React.FC<VideoModelConfigFieldsProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const config = parse(value);
  if (!config) {
    return (
      <Alert severity="warning" className="md:col-span-2">
        Fix the Config Override JSON to edit the video settings.
      </Alert>
    );
  }
  const video = isJsonObject(config.video) ? config.video : {};

  const update = (key: string, next: unknown) => {
    const nextVideo: JsonObject = { ...video, [key]: next };
    if (next === undefined || next === '' || (Array.isArray(next) && next.length === 0)) {
      delete nextVideo[key];
    }
    const nextConfig: JsonObject = { ...config, video: nextVideo };
    if (Object.keys(nextVideo).length === 0) delete nextConfig.video;
    onChange(Object.keys(nextConfig).length ? JSON.stringify(nextConfig, null, 2) : '');
  };

  return (
    <Box className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-slate-700 md:col-span-2">
      <Typography variant="subtitle2" className="text-gray-700 dark:text-slate-300">
        Video settings for this model
      </Typography>
      <Box className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextField
          size="small"
          label="Duration options (s)"
          defaultValue={joinList(video.durationSecondsOptions)}
          onBlur={(event) =>
            update('durationSecondsOptions', parsePositiveIntList(event.target.value) ?? undefined)
          }
          helperText="Subset of the capability options, e.g. 5, 8"
          disabled={disabled}
        />
        <TextField
          size="small"
          label="Aspect ratios"
          defaultValue={joinList(video.aspectRatioOptions)}
          onBlur={(event) => update('aspectRatioOptions', splitList(event.target.value))}
          helperText="e.g. 16:9, 9:16"
          disabled={disabled}
        />
        <TextField
          size="small"
          label="Resolution label"
          defaultValue={typeof video.resolutionLabel === 'string' ? video.resolutionLabel : ''}
          onBlur={(event) => update('resolutionLabel', event.target.value.trim())}
          helperText="Shown to users, e.g. 720p (price per resolution = separate model)"
          disabled={disabled}
        />
        <TextField
          size="small"
          type="number"
          label="Max input images"
          defaultValue={typeof video.maxInputImages === 'number' ? video.maxInputImages : ''}
          onBlur={(event) => {
            const number = Number(event.target.value);
            update('maxInputImages', Number.isInteger(number) && number > 0 ? number : undefined);
          }}
          helperText="image_to_video only"
          disabled={disabled}
        />
        <FormControl size="small" disabled={disabled}>
          <InputLabel id="video-image-input">Image input</InputLabel>
          <Select
            labelId="video-image-input"
            label="Image input"
            value={typeof video.imageInput === 'string' ? video.imageInput : ''}
            onChange={(event) => update('imageInput', event.target.value || undefined)}
          >
            <MenuItem value="">Default (bytes)</MenuItem>
            <MenuItem value="bytes">bytes — base64 sent by the backend</MenuItem>
            <MenuItem value="signed_url">signed_url — short-lived R2 URL</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={video.requirePrompt === true}
              onChange={(_event, checked) => update('requirePrompt', checked || undefined)}
              disabled={disabled}
            />
          }
          label="Prompt required (image_to_video)"
        />
      </Box>
    </Box>
  );
};
